import type { PopupRoundConfig } from '@coorum-research/shared-common/types/popup.type' // あとで、LauncherPopup.tsxに統合する。
import type { CSSProperties, MouseEvent, RefObject } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CloseButton } from './CloseButton'
import { PresetIcon } from './icons/PresetIcon'

type Props = {
  configJson?: PopupRoundConfig | null
  onClick?: () => void
  onClose?: () => void
  isEmbed?: boolean
  ref?: RefObject<HTMLDivElement | null>
}

export function LauncherPopupRound({ ref, configJson, onClick, onClose, isEmbed }: Props) {
  const {
    title,
    horizontal,
    vertical,
    displayPosition,
    theme,
  } = configJson ?? {}

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [closePositionSet, setClosePositionSet] = useState<Pick<CSSProperties, 'bottom' | 'left' | 'right'> | null>(null)

  const displayPositionSet = useMemo(() => {
    if (displayPosition === 'bottom-right') {
      return {
        bottom: `${vertical}px`,
        right: `${horizontal}px`,
      }
    } else if (displayPosition === 'bottom-left') {
      return {
        bottom: `${vertical}px`,
        left: `${horizontal}px`,
      }
    }
  }, [displayPosition, horizontal, vertical])

  // 右下、左下の値を連動して、.cr-research-launcherを bottom 0, right 0 または bottom 0, left 0 になるようにする
  useEffect(() => {
    // window.parent.document.getElementById('cr-research-launcher')
    const launcherEl = window.parent.document.getElementById('cr-research-launcher') || window.document.querySelector('.cr-research-launcher')
    if (!launcherEl) {
      return
    }

    if (displayPosition === 'bottom-right') {
      launcherEl?.style.setProperty('bottom', '0')
      launcherEl?.style.setProperty('right', '0')
    } else if (displayPosition === 'bottom-left') {
      launcherEl?.style.setProperty('bottom', '0')
      launcherEl?.style.setProperty('left', '0')
    }
  }, [displayPosition, displayPositionSet])

  useEffect(() => {
    if (!containerRef?.current) {
      return
    }

    const closeBtnMargin = 16 / 2
    const v = vertical ?? 0
    const h = horizontal ?? 0
    const width = containerRef.current.clientWidth
    const height = containerRef.current.clientHeight

    if (displayPosition === 'bottom-right') {
      setClosePositionSet({
        bottom: `${v + height - closeBtnMargin}px`,
        right: `${h - closeBtnMargin}px`,
      })
    } else if (displayPosition === 'bottom-left') {
      setClosePositionSet({
        bottom: `${v + height - closeBtnMargin}px`,
        left: `${h + width - closeBtnMargin}px`,
      })
    }
  }, [displayPosition, vertical, horizontal, containerRef])

  const handleClose = (e: MouseEvent) => {
    e.stopPropagation()

    if (onClose) {
      onClose()
    }
  }

  return (
    <>
      <div
        ref={(node) => {
          containerRef.current = node
          if (ref) {
            ref.current = node
          }
        }}
        style={{
          ...displayPositionSet,
          position: isEmbed ? 'static' : 'absolute',
          overflow: 'hidden',
          width: 59,
          height: 59,
          boxShadow: '0 1px 6px 0 rgba(0, 0, 0, 0.06), 0 2px 32px 0 rgba(0, 0, 0, 0.16)',
          transition: 'transform 167ms cubic-bezier(0.33, 0.00, 0.00, 1.00)',
          boxSizing: 'content-box',
          borderRadius: '100%',
        }}
      >
        <button
          type="button"
          style={{
            cursor: 'pointer',
            padding: 0,
            margin: 0,
            display: 'block',
            width: '100%',
            height: '100%',
            backgroundColor: theme?.mainColor,
            border: 'none',
          }}
          onClick={onClick}
        >
          {title
            ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <PresetIcon
                  presetIcon={configJson?.presetIcon}
                  width={24}
                  height={24}
                  fill={theme?.mainTextColor}
                />
                <p
                  style={{
                    color: theme?.mainTextColor,
                    fontSize: '9px',
                    fontWeight: 700,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {title}
                </p>
              </div>
            )
            : (
              <PresetIcon
                presetIcon={configJson?.presetIcon}
                width={24}
                height={24}
                fill={theme?.mainTextColor}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              />
            )}
        </button>
      </div>
      {configJson?.showCloseButton && onClose && closePositionSet && (
        <CloseButton
          theme="black"
          style={{ position: 'absolute', fontSize: '12px', ...closePositionSet }}
          onClick={handleClose}
        />
      )}
    </>
  )
}
